import { CareerProfileFieldsFragment, MyCareerProfileQuery } from 'core';
import {
  CAREER_SKILL_SOURCE_COURSE,
  CAREER_SKILL_SOURCE_SELF,
  ICvLocale,
  LanguageLevel,
} from 'context';
import { ICareerProfileFormValues } from 'organisms';

import { monthNumberToName, yearToString } from './monthBridge';

export interface ICareerProfileData {
  values: ICareerProfileFormValues;
  locales: ICvLocale[];
  completeness: number;
  isSearchable: boolean;
  contactsVisible: boolean;
  photoUrl: string | null;
}

/**
 * Maps a `CareerProfileType` node into the editor's view model (spec 0018,
 * increment B): integer years/months become the picker's string form, a project's
 * parallel `technologyIds`/`technologyNames` arrays become chips, and each
 * certificate keeps its backend id (`serverId`) so it can be reconciled on save.
 *
 * Shared by the `myCareerProfile` read and the `saveCareerProfile` response so a
 * save can reset the form baseline from the mutation result without a refetch.
 */
export const mapProfileNode = (profile: CareerProfileFieldsFragment): ICareerProfileData => ({
  values: {
    firstName: profile.firstName,
    lastName: profile.lastName,
    email: profile.email,
    phone: profile.phone,
    city: profile.city,
    country: profile.country,
    relocationReady: profile.relocationReady,
    workMode: profile.workMode,
    workPermit: profile.workPermit,
    availableFrom: profile.availableFrom ?? '',
    seniority: profile.seniority,
    desiredPosition: profile.desiredPosition,
    desiredSalary: profile.desiredSalary,
    githubUrl: profile.githubUrl,
    linkedinUrl: profile.linkedinUrl,
    portfolioUrl: profile.portfolioUrl,
    skills: profile.skills.map((skill) => ({
      skillId: skill.skillId,
      name: skill.name,
      slug: skill.slug,
      iconUrl: skill.iconUrl ?? null,
      category: skill.category,
      level: skill.level,
      years: skill.years ?? null,
      // ТЗ 0039. Сервер отдаёт строку; сужаем её здесь, а не в компоненте,
      // чтобы неизвестное значение читалось как «своё», а не роняло вид.
      source:
        skill.source === CAREER_SKILL_SOURCE_COURSE
          ? CAREER_SKILL_SOURCE_COURSE
          : CAREER_SKILL_SOURCE_SELF,
      isHidden: Boolean(skill.isHidden),
      grantedCourseName: skill.grantedCourseName ?? null,
      grantedLessonNumber: skill.grantedLessonNumber ?? null,
    })),
    customSkills: [...profile.customSkills],
    projects: profile.projects.map((project) => ({
      id: project.id,
      title: project.title,
      description: project.description,
      role: project.role,
      repoUrl: project.repoUrl,
      demoUrl: project.demoUrl,
      technologies: project.technologyIds.map((id, index) => ({
        id,
        name: project.technologyNames[index] ?? id,
      })),
    })),
    experience: profile.experience.map((item) => ({
      id: item.id,
      company: item.company,
      position: item.position,
      description: item.description,
      startDate: { year: yearToString(item.startYear), month: monthNumberToName(item.startMonth) },
      endDate: { year: yearToString(item.endYear), month: monthNumberToName(item.endMonth) },
      isCurrent: item.isCurrent,
    })),
    education: profile.education.map((item) => ({
      id: item.id,
      institution: item.institution,
      degree: item.degree,
      specialization: item.specialization,
      startDate: { year: yearToString(item.startYear), month: monthNumberToName(item.startMonth) },
      endDate: { year: yearToString(item.endYear), month: monthNumberToName(item.endMonth) },
    })),
    languages: profile.languages.map((item) => ({
      id: item.id,
      language: item.language,
      level: (item.level as LanguageLevel) || '',
    })),
    certificates: profile.certificates.map((item) => ({
      id: item.id,
      serverId: item.id,
      title: item.title,
      issuer: item.issuer,
      issuedAt: item.issuedAt ?? '',
      url: item.url,
      fileUrl: item.fileUrl ?? null,
    })),
  },
  locales: profile.locales.map((locale) => ({
    language: locale.language,
    jobTitle: locale.jobTitle,
    about: locale.about,
  })),
  completeness: profile.completeness,
  isSearchable: profile.isSearchable,
  contactsVisible: profile.contactsVisible,
  photoUrl: profile.photoUrl ?? null,
});

/** Adapts the `myCareerProfile` query payload. */
export const transformCareerProfile = (
  data: MyCareerProfileQuery | undefined,
): ICareerProfileData | null =>
  data?.myCareerProfile ? mapProfileNode(data.myCareerProfile) : null;
